import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Calendar,
  Clock,
  Plus,
  ChevronLeft,
  ChevronRight,
  X,
  Check,
  Trash2,
  Copy,
  Settings,
  Users,
  Globe,
  Link2,
  ExternalLink,
  Edit2,
  MoreVertical,
  Save,
  RefreshCw,
  History,
  ArrowRight,
  Lock as LockIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../utils/api';
import { StatusBadge } from '../components/StatusBadge';
import { AutomationLogModal } from '../components/calendar/AutomationLogModal';
import { QUESTION_PRESETS, QUESTION_TYPES, slugifyKey } from '../lib/customQuestions';

const API_URL = getApiUrl();

const EVENT_COLORS = [
  { name: 'Indigo', bg: 'bg-indigo-500', light: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-500' },
  { name: 'Emerald', bg: 'bg-emerald-500', light: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-500' },
  { name: 'Rose', bg: 'bg-rose-500', light: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-500' },
  { name: 'Amber', bg: 'bg-amber-500', light: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-500' },
  { name: 'Cyan', bg: 'bg-cyan-500', light: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-500' },
];

const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const defaultWeeklySchedule = {
  sunday: { enabled: false, slots: [] },
  monday: { enabled: true, slots: [{ start: '09:00', end: '17:00' }] },
  tuesday: { enabled: true, slots: [{ start: '09:00', end: '17:00' }] },
  wednesday: { enabled: true, slots: [{ start: '09:00', end: '17:00' }] },
  thursday: { enabled: true, slots: [{ start: '09:00', end: '17:00' }] },
  friday: { enabled: true, slots: [{ start: '09:00', end: '17:00' }] },
  saturday: { enabled: false, slots: [] }
};

const CalendarPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin';
  const isTeamLead = user?.role === 'sales' && !!user?.is_team_lead;
  const canSeeAllSales = isAdmin || isTeamLead;               // TL / admin: every sales booking + Associate column
  const [upcomingSalesRep, setUpcomingSalesRep] = useState('all'); // all | me | user_id
  const [reassignTo, setReassignTo] = useState('');
  const [reassigning, setReassigning] = useState(false);
  // V45.5 — Sales reps get access to the full Calendar workspace (event-types
  // + slots + bookings) so they can manage availability. Guards below split
  // read/edit rights from create/delete rights.
  const canAccessCalendarAdmin = user?.role === 'super_admin' || user?.role === 'sales';
  const [activeView, setActiveView] = useState(canAccessCalendarAdmin ? 'event-types' : 'bookings');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [automationLogEvent, setAutomationLogEvent] = useState(null); // event type whose automation log is open
  const [selectedEventType, setSelectedEventType] = useState(null);
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [bookingFilter, setBookingFilter] = useState('upcoming'); // 'upcoming' | 'past' (V36.6)
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // V32 — Past-tab filters: date preset + custom range + sales rep
  const [pastDatePreset, setPastDatePreset] = useState('mtd'); // today | yesterday | mtd | last_month | custom
  const [pastDateFrom, setPastDateFrom] = useState(''); // YYYY-MM-DD (only used when preset=custom)
  const [pastDateTo, setPastDateTo] = useState('');
  const [pastSalesRep, setPastSalesRep] = useState('all'); // user_id or 'all'
  const [salesUsers, setSalesUsers] = useState([]); // list of sales/pre-sales users for the dropdown
  const [pastBucketModal, setPastBucketModal] = useState(null); // {key, label, leads:[]}

  const [upcomingDatePreset, setUpcomingDatePreset] = useState('all'); // all | today | tomorrow | this_week | custom
  const [upcomingDateFrom, setUpcomingDateFrom] = useState('');
  const [upcomingDateTo, setUpcomingDateTo] = useState('');
  const [bookingSortBy, setBookingSortBy] = useState('datetime'); // datetime | customer | status
  const [bookingSortDir, setBookingSortDir] = useState('asc'); // asc | desc
  const [bookingPage, setBookingPage] = useState(1);
  const BOOKING_PAGE_SIZE = 25;

  // V36.6 — Reset page to 1 whenever any booking-tab filter changes
  useEffect(() => {
    setBookingPage(1);
  }, [bookingFilter, upcomingDatePreset, upcomingDateFrom, upcomingDateTo,
       pastDatePreset, pastDateFrom, pastDateTo, pastSalesRep,
       bookingSortBy, bookingSortDir]);


  // Modals
  const [showEventTypeModal, setShowEventTypeModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editingEventType, setEditingEventType] = useState(null);

  // Reschedule state
  const [rescheduleMode, setRescheduleMode] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);

  // V36.9 — Full consultation history for the lead in the open Booking Details modal
  const [bookingHistory, setBookingHistory] = useState([]);
  const [transferHistory, setTransferHistory] = useState([]);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const saveSalesNotes = async () => {
    if (!selectedBooking) return;
    setSavingNotes(true);
    try {
      const res = await axios.patch(
        `${API_URL}/api/calendar/bookings/${selectedBooking.booking_id}/sales-notes`,
        { sales_notes: notesDraft }, { withCredentials: true });
      setSelectedBooking({
        ...selectedBooking,
        outcome_reason: res.data.sales_notes,
        sales_notes_updated_by_name: res.data.updated_by,
      });
      setEditingNotes(false);
      toast.success('Sales notes saved');
      fetchAllBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the note');
    } finally {
      setSavingNotes(false);
    }
  };
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Event type form
  const [eventTypeForm, setEventTypeForm] = useState({
    name: '',
    slug: '',
    description: '',
    duration_minutes: 30,
    advance_booking_days: 14,
    minimum_notice_hours: 24,
    max_bookings_per_day: null,
    allow_group_booking: false,
    max_group_size: 1,
    weekly_schedule: defaultWeeklySchedule,
    timezone: 'Asia/Kolkata',
    color: 0,
    country_filter: '',
    holidays: [],
    page_subtitle: '',
    cta_text: '',
    confirmation_title: '',
    confirmation_subtitle: '',
    whats_next_title: '',
    whats_next_items: [],
    meeting_link: '',
    whatsapp_automations: [],
    confirmation_buttons: [],
    custom_questions: []
  });

  // Holiday form
  const [newHolidayDate, setNewHolidayDate] = useState('');

  // V40 — WhatsApp automation test (phone per automation row)
  const [waTestPhone, setWaTestPhone] = useState({});
  const [waTestingId, setWaTestingId] = useState(null);

  const WA_TRIGGERS = [
    { value: 'immediate', label: 'On booking confirmation (immediate)' },
    { value: '1_day', label: '1 day before' },
    { value: '60_min', label: '60 minutes before' },
    { value: '10_min', label: '10 minutes before' },
  ];

  // Generate form (legacy - keeping for backwards compatibility)
  const [generateForm, setGenerateForm] = useState({
    date_from: '',
    date_to: ''
  });

  useEffect(() => {
    fetchEventTypes();
    // V32 — load sales/pre-sales users for the Past-tab rep filter
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/users/associates`, { withCredentials: true });
        // V32.1 — Sales-rep filter must show ONLY actual sales associates
        // (per user direction: "not pre sales or admin"). The lead's
        // `assigned_sales_rep_id` is what we filter against, and that's only
        // populated for users with the `sales` role.
        const list = (res.data || []).filter(u => u.role === 'sales' && u.is_active !== false);
        setSalesUsers(list);
      } catch {
        // non-fatal — dropdown will just show "All"
      }
    })();
  }, []);

  // V32 — Resolve the active date range from the preset (Today/Yesterday/MTD/Last Month/Custom).
  // Returns YYYY-MM-DD strings. For "custom", uses the explicit inputs (may be empty).
  const getPastDateRange = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fmt = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    if (pastDatePreset === 'today') {
      return { from: fmt(today), to: fmt(today) };
    }
    if (pastDatePreset === 'yesterday') {
      const y = new Date(today); y.setDate(y.getDate() - 1);
      return { from: fmt(y), to: fmt(y) };
    }
    if (pastDatePreset === 'mtd') {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: fmt(first), to: fmt(today) };
    }
    if (pastDatePreset === 'last_month') {
      const firstPrev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastPrev = new Date(today.getFullYear(), today.getMonth(), 0); // day 0 of current month = last day of previous
      return { from: fmt(firstPrev), to: fmt(lastPrev) };
    }
    // custom
    return { from: pastDateFrom || '', to: pastDateTo || '' };
  };

  // V32 — Apply the Past-tab filters to the bookings list: date range, sales rep,
  // status=past (date+time has elapsed AND not cancelled). Returns the filtered array.
  const applyPastFilters = (rows) => {
    const { from, to } = getPastDateRange();
    const now = new Date();
    return rows.filter(b => {
      // 1. Must be a "past" booking: time has elapsed OR explicitly marked completed/wip/not_interested/no_show/paid
      const [h, m] = (b.booking_time || '00:00').split(':');
      const bDT = new Date(b.booking_date);
      bDT.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
      const isPastOrSettled = bDT <= now || ['completed', 'wip', 'not_interested', 'no_show', 'paid'].includes(b.status);
      if (!isPastOrSettled) return false;
      if (b.status === 'cancelled') return false;
      // 2. Date range filter (booking_date is YYYY-MM-DD)
      if (from && b.booking_date < from) return false;
      if (to && b.booking_date > to) return false;
      // 3. Sales rep filter — uses LEAD's assigned sales rep, not the booking's
      // booker (V32.1 — booker is usually pre-sales; sales rep takes the call)
      if (pastSalesRep && pastSalesRep !== 'all' && b.lead_assigned_sales_rep_id !== pastSalesRep) return false;
      return true;
    });
  };

  // V36.9 — De-dupe a booking list to ONE unique row per lead.
  // Key priority: lead_id → lowercased customer_email → booking_id.
  // `primary` decides which consultation represents the lead in the visible row:
  //   'latest'   → most recent consultation (Past tab — shows the final outcome)
  //   'earliest' → soonest consultation (Upcoming tab — shows the next call)
  // The full ordered group is attached as `_history` (newest-first) + `_groupCount`
  // so the Booking Details modal can surface every prior / rescheduled consultation.
  const dedupeByLead = (rows, primary = 'latest') => {
    const groups = new Map();
    for (const b of rows) {
      const key = b.lead_id || (b.customer_email || '').toLowerCase().trim() || b.booking_id;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(b);
    }
    const out = [];
    groups.forEach((arr) => {
      const asc = [...arr].sort((a, b) => {
        const dtA = new Date(`${a.booking_date}T${a.booking_time || '00:00'}`);
        const dtB = new Date(`${b.booking_date}T${b.booking_time || '00:00'}`);
        return dtA - dtB; // oldest → newest
      });
      const rep = primary === 'earliest' ? asc[0] : asc[asc.length - 1];
      out.push({ ...rep, _history: [...asc].reverse(), _groupCount: arr.length });
    });
    return out;
  };

  // V36.9 — Single source of truth for a lead's consultation OUTCOME.
  // "Enrolled" is derived from the lead's REAL enrollment payment
  // (enrollment_status enrolled/renewed OR lead_status enrolled) — never from
  // the booking's manual status, which no flow ever sets to "paid". Trials do
  // NOT count as enrolled. Every lead maps to exactly one bucket (WIP = catch-all),
  // so the four cards always sum to the unique-lead total (no silent drops).
  const classifyOutcome = (b) => {
    const es = (b.lead_enrollment_status || '').toLowerCase();
    const ls = (b.lead_status || '').toLowerCase();
    const bs = (b.status || '').toLowerCase();
    if (es === 'enrolled' || es === 'renewed' || ls === 'enrolled') return 'paid';
    if (ls === 'not_interested' || bs === 'not_interested') return 'not_interested';
    if (ls === 'no_show' || bs === 'no_show') return 'no_show';
    // V52 — WIP only when a rep actually actioned it (booking marked wip or lead in wip);
    // anything untouched is PENDING so unactioned consultations no longer masquerade as WIP.
    if (bs === 'wip' || ls === 'wip') return 'wip';
    return 'pending';
  };

  // Map an outcome bucket key → a StatusBadge status token (for the Past table).
  const OUTCOME_STATUS = { paid: 'enrolled', no_show: 'no_show', not_interested: 'not_interested', wip: 'wip', pending: 'pending' };

  // V36.9 — Bucket the filtered past bookings by lead OUTCOME. De-dupes by lead
  // (latest consultation represents the lead) and classifies each unique lead
  // into exactly one of the four cards. Sum of cards == unique-lead count.
  const bucketPastBookings = (rows) => {
    const unique = dedupeByLead(rows, 'latest');
    const buckets = { pending: [], wip: [], not_interested: [], no_show: [], paid: [] };
    for (const b of unique) {
      buckets[classifyOutcome(b)].push(b);
    }
    return buckets;
  };

  useEffect(() => {
    if (selectedEventType) {
      fetchSlotsAndBookings();
    }
    // Always fetch bookings for the Bookings tab (works with or without event type)
    fetchAllBookings();
  }, [selectedEventType, currentMonth]);

  const fetchEventTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/calendar/event-types`, { withCredentials: true });
      setEventTypes(response.data);
      if (response.data.length > 0 && !selectedEventType) {
        setSelectedEventType(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching event types:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBookings = async () => {
    try {
      // Fetch bookings for a wide range: 90 days back + 90 days ahead
      const now = new Date();
      const past = new Date(now);
      past.setDate(past.getDate() - 90);
      const future = new Date(now);
      future.setDate(future.getDate() + 90);
      const dateFrom = formatDate(past);
      const dateTo = formatDate(future);

      let url = `${API_URL}/api/calendar/bookings?date_from=${dateFrom}&date_to=${dateTo}`;
      // Only filter by event type if one is selected (not "All Events")
      if (selectedEventType) {
        url += `&event_type_id=${selectedEventType.event_type_id}`;
      }
      const res = await axios.get(url, { withCredentials: true });
      setAllBookings(res.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const fetchSlotsAndBookings = async () => {
    if (!selectedEventType) return;
    
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const dateFrom = formatDate(firstDay);
      const dateTo = formatDate(lastDay);

      const [slotsRes, bookingsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/calendar/slots?event_type_id=${selectedEventType.event_type_id}&date_from=${dateFrom}&date_to=${dateTo}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/calendar/bookings?event_type_id=${selectedEventType.event_type_id}&date_from=${dateFrom}&date_to=${dateTo}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/calendar/stats?event_type_id=${selectedEventType.event_type_id}`, { withCredentials: true })
      ]);

      setSlots(slotsRes.data);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    }
  };

  const formatDate = (date) => date.toISOString().split('T')[0];

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Convert a time string (HH:MM) on a given date from source tz to target tz
  const convertTimezone = (dateStr, timeStr, sourceTz, targetTz) => {
    if (!dateStr || !timeStr || !sourceTz || !targetTz || sourceTz === targetTz) return null;
    try {
      const [hours, minutes] = timeStr.split(':').map(Number);
      // Build a Date in the source timezone using Intl
      const dateInSource = new Date(`${dateStr}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`);
      // Get the UTC offset for source timezone
      const sourceFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: sourceTz, hour: 'numeric', minute: 'numeric', hour12: true
      });
      const targetFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: targetTz, hour: 'numeric', minute: 'numeric', hour12: true
      });
      // Use a known UTC reference approach
      const utcDate = new Date(dateInSource.toLocaleString('en-US', { timeZone: sourceTz }));
      const diff = dateInSource - utcDate;
      const utcTime = new Date(dateInSource.getTime() + diff);
      return targetFormatter.format(utcTime);
    } catch {
      return null;
    }
  };

  // Get short timezone label (e.g., "EST", "PST")
  const getTimezoneAbbr = (tz) => {
    if (!tz) return '';
    try {
      const formatter = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' });
      const parts = formatter.formatToParts(new Date());
      const tzPart = parts.find(p => p.type === 'timeZoneName');
      return tzPart ? tzPart.value : tz.split('/').pop().replace('_', ' ');
    } catch {
      return tz.split('/').pop().replace('_', ' ');
    }
  };

  const slugify = (text) => {
    return text.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/[\s_-]+/g, '-').trim();
  };

  const copyBookingLink = (slug) => {
    const url = `${window.location.origin}/book/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Booking link copied!');
  };

  // Time validation helpers
  const timeToMinutes = (time) => {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };

  const validateTimeSlots = (weeklySchedule) => {
    const errors = [];
    
    for (const [day, config] of Object.entries(weeklySchedule)) {
      if (!config.enabled || !config.slots || config.slots.length === 0) continue;
      
      const dayLabel = day.charAt(0).toUpperCase() + day.slice(1);
      
      // Check each slot
      for (let i = 0; i < config.slots.length; i++) {
        const slot = config.slots[i];
        const startMins = timeToMinutes(slot.start);
        const endMins = timeToMinutes(slot.end);
        
        // Check if end time is after start time
        if (endMins <= startMins) {
          errors.push(`${dayLabel}: Slot ${i + 1} has end time (${formatTime(slot.end)}) before or equal to start time (${formatTime(slot.start)})`);
        }
        
        // Check for overlaps with other slots on the same day
        for (let j = i + 1; j < config.slots.length; j++) {
          const otherSlot = config.slots[j];
          const otherStartMins = timeToMinutes(otherSlot.start);
          const otherEndMins = timeToMinutes(otherSlot.end);
          
          // Check if slots overlap
          if (!(endMins <= otherStartMins || otherEndMins <= startMins)) {
            errors.push(`${dayLabel}: Slot ${i + 1} (${formatTime(slot.start)} - ${formatTime(slot.end)}) overlaps with Slot ${j + 1} (${formatTime(otherSlot.start)} - ${formatTime(otherSlot.end)})`);
          }
        }
      }
    }
    
    return errors;
  };

  // Event Type CRUD
  const handleSaveEventType = async () => {
    // Validate required fields
    if (!eventTypeForm.name.trim()) {
      toast.error('Event name is required');
      return;
    }
    
    // Validate time slots
    const validationErrors = validateTimeSlots(eventTypeForm.weekly_schedule);
    if (validationErrors.length > 0) {
      toast.error(validationErrors[0]); // Show first error
      return;
    }
    
    try {
      const data = {
        ...eventTypeForm,
        slug: eventTypeForm.slug || slugify(eventTypeForm.name),
        country_filter: eventTypeForm.country_filter || null,
        max_bookings_per_day: eventTypeForm.max_bookings_per_day || null,
        // V40 — booking-page content + WhatsApp automations (normalize blanks)
        page_subtitle: eventTypeForm.page_subtitle?.trim() || null,
        cta_text: eventTypeForm.cta_text?.trim() || null,
        confirmation_title: eventTypeForm.confirmation_title?.trim() || null,
        confirmation_subtitle: eventTypeForm.confirmation_subtitle?.trim() || null,
        whats_next_title: eventTypeForm.whats_next_title?.trim() || null,
        whats_next_items: (eventTypeForm.whats_next_items || []).map(s => s.trim()).filter(Boolean),
        meeting_link: eventTypeForm.meeting_link?.trim() || null,
        whatsapp_automations: (eventTypeForm.whatsapp_automations || [])
          .filter(a => (a.template_name || '').trim())
          .map(a => ({ id: a.id, enabled: !!a.enabled, trigger: a.trigger, template_name: a.template_name.trim() })),
        confirmation_buttons: (eventTypeForm.confirmation_buttons || [])
          .filter(b => (b.label || '').trim() && (b.url || '').trim())
          .map(b => ({ id: b.id || `cbtn_${Date.now()}`, label: b.label.trim(), url: b.url.trim() })),
        custom_questions: (eventTypeForm.custom_questions || [])
          .filter(q => (q.label || '').trim())
          .map(q => ({
            id: q.id || `q_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
            key: (q.key || '').trim() || slugifyKey(q.label),
            label: q.label.trim(),
            type: q.type || 'text',
            options: q.type === 'select' ? (q.options || []).map(o => (o || '').trim()).filter(Boolean) : [],
            required: !!q.required,
          })),
      };

      if (editingEventType) {
        await axios.put(`${API_URL}/api/calendar/event-types/${editingEventType.event_type_id}`, data, { withCredentials: true });
        toast.success('Event type updated');
      } else {
        await axios.post(`${API_URL}/api/calendar/event-types`, data, { withCredentials: true });
        toast.success('Event type created');
      }

      setShowEventTypeModal(false);
      setEditingEventType(null);
      resetEventTypeForm();
      fetchEventTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save event type');
    }
  };

  const handleEditEventType = (eventType) => {
    setEditingEventType(eventType);
    setEventTypeForm({
      name: eventType.name,
      slug: eventType.slug,
      description: eventType.description || '',
      duration_minutes: eventType.duration_minutes,
      advance_booking_days: eventType.advance_booking_days,
      minimum_notice_hours: eventType.minimum_notice_hours,
      max_bookings_per_day: eventType.max_bookings_per_day,
      allow_group_booking: eventType.allow_group_booking || false,
      max_group_size: eventType.max_group_size || 1,
      weekly_schedule: eventType.weekly_schedule || defaultWeeklySchedule,
      timezone: eventType.timezone,
      color: eventType.color,
      country_filter: eventType.country_filter || '',
      holidays: eventType.holidays || [],
      page_subtitle: eventType.page_subtitle || '',
      cta_text: eventType.cta_text || '',
      confirmation_title: eventType.confirmation_title || '',
      confirmation_subtitle: eventType.confirmation_subtitle || '',
      whats_next_title: eventType.whats_next_title || '',
      whats_next_items: eventType.whats_next_items || [],
      meeting_link: eventType.meeting_link || '',
      whatsapp_automations: eventType.whatsapp_automations || [],
      confirmation_buttons: eventType.confirmation_buttons || [],
      custom_questions: eventType.custom_questions || []
    });
    setShowEventTypeModal(true);
  };

  const handleDeleteEventType = async (eventTypeId) => {
    if (!window.confirm('Are you sure you want to deactivate this event type?')) return;
    try {
      await axios.delete(`${API_URL}/api/calendar/event-types/${eventTypeId}`, { withCredentials: true });
      toast.success('Event type deactivated');
      fetchEventTypes();
    } catch (error) {
      toast.error('Failed to delete event type');
    }
  };

  // V40 — WhatsApp automation row helpers
  const addWhatsAppAutomation = () => {
    setEventTypeForm(prev => ({
      ...prev,
      whatsapp_automations: [
        ...(prev.whatsapp_automations || []),
        { id: `wa_${Date.now()}`, enabled: true, trigger: 'immediate', template_name: '' },
      ],
    }));
  };

  const updateWhatsAppAutomation = (id, patch) => {
    setEventTypeForm(prev => ({
      ...prev,
      whatsapp_automations: (prev.whatsapp_automations || []).map(a => a.id === id ? { ...a, ...patch } : a),
    }));
  };

  const removeWhatsAppAutomation = (id) => {
    setEventTypeForm(prev => ({
      ...prev,
      whatsapp_automations: (prev.whatsapp_automations || []).filter(a => a.id !== id),
    }));
  };

  // V41 — Confirmation-page CTA button row helpers (label + url)
  const addConfirmationButton = () => {
    setEventTypeForm(prev => ({
      ...prev,
      confirmation_buttons: [
        ...(prev.confirmation_buttons || []),
        { id: `cbtn_${Date.now()}`, label: '', url: '' },
      ],
    }));
  };

  const updateConfirmationButton = (idx, patch) => {
    setEventTypeForm(prev => {
      const arr = [...(prev.confirmation_buttons || [])];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...prev, confirmation_buttons: arr };
    });
  };

  const removeConfirmationButton = (idx) => {
    setEventTypeForm(prev => ({
      ...prev,
      confirmation_buttons: (prev.confirmation_buttons || []).filter((_, i) => i !== idx),
    }));
  };

  // V42 — Custom question builder helpers
  const addCustomQuestion = () => {
    setEventTypeForm(prev => ({
      ...prev,
      custom_questions: [
        ...(prev.custom_questions || []),
        { id: `q_${Date.now()}`, key: '', label: '', type: 'text', options: [], required: false },
      ],
    }));
  };

  const updateCustomQuestion = (idx, patch) => {
    setEventTypeForm(prev => {
      const arr = [...(prev.custom_questions || [])];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...prev, custom_questions: arr };
    });
  };

  const applyQuestionPreset = (idx, presetKey) => {
    const p = QUESTION_PRESETS.find(x => x.preset === presetKey);
    if (!p) return;
    updateCustomQuestion(idx, {
      preset: presetKey,
      key: p.key,
      label: p.label,
      type: p.type,
      options: [...p.options],
    });
  };

  const removeCustomQuestion = (idx) => {
    setEventTypeForm(prev => ({
      ...prev,
      custom_questions: (prev.custom_questions || []).filter((_, i) => i !== idx),
    }));
  };

  const handleTestWhatsApp = async (automation) => {
    if (!editingEventType) {
      toast.error('Save the event type first, then send a test');
      return;
    }
    const phone = (waTestPhone[automation.id] || '').trim();
    if (!phone) { toast.error('Enter a test phone number (with country code)'); return; }
    if (!(automation.template_name || '').trim()) { toast.error('Enter the WATI template name first'); return; }
    setWaTestingId(automation.id);
    try {
      await axios.post(
        `${API_URL}/api/calendar/event-types/${editingEventType.event_type_id}/test-whatsapp`,
        { phone, template_name: automation.template_name.trim() },
        { withCredentials: true }
      );
      toast.success(`Test WhatsApp sent to ${phone}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test failed — check the template name in WATI');
    } finally {
      setWaTestingId(null);
    }
  };


  const resetEventTypeForm = () => {
    setEventTypeForm({
      name: '',
      slug: '',
      description: '',
      duration_minutes: 30,
      advance_booking_days: 14,
      minimum_notice_hours: 24,
      max_bookings_per_day: null,
      allow_group_booking: false,
      max_group_size: 1,
      weekly_schedule: defaultWeeklySchedule,
      timezone: 'Asia/Kolkata',
      color: 0,
      country_filter: '',
      holidays: [],
      page_subtitle: '',
      cta_text: '',
      confirmation_title: '',
      confirmation_subtitle: '',
      whats_next_title: '',
      whats_next_items: [],
      meeting_link: '',
      whatsapp_automations: [],
      confirmation_buttons: [],
      custom_questions: []
    });
  };

  // Holiday management
  const handleAddHoliday = () => {
    if (!newHolidayDate) return;
    if (eventTypeForm.holidays.includes(newHolidayDate)) {
      toast.error('This date is already marked as a holiday');
      return;
    }
    const updatedHolidays = [...eventTypeForm.holidays, newHolidayDate].sort();
    setEventTypeForm({ ...eventTypeForm, holidays: updatedHolidays });
    setNewHolidayDate('');
    toast.success('Holiday added');
  };

  const handleRemoveHoliday = (date) => {
    setEventTypeForm({ 
      ...eventTypeForm, 
      holidays: eventTypeForm.holidays.filter(d => d !== date) 
    });
  };

  // Generate slots
  const handleGenerateSlots = async () => {
    if (!selectedEventType) return;
    try {
      const response = await axios.post(
        `${API_URL}/api/calendar/generate-slots?event_type_id=${selectedEventType.event_type_id}&date_from=${generateForm.date_from}&date_to=${generateForm.date_to}`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setShowGenerateModal(false);
      fetchSlotsAndBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate slots');
    }
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      await axios.delete(`${API_URL}/api/calendar/slots/${slotId}`, { withCredentials: true });
      toast.success('Slot deleted');
      fetchSlotsAndBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cannot delete slot with bookings');
    }
  };

  const [actionMode, setActionMode] = useState(null); // 'wip', 'not_interested', 'no_show'
  const [actionNotes, setActionNotes] = useState('');
  const [callbackDate, setCallbackDate] = useState('');
  const [callbackTime, setCallbackTime] = useState('');

  // V36.9 — Open the Booking Details modal and load the lead's full consultation
  // history (every booking across all event types/dates) so rescheduled and
  // repeat consultations stay visible even though the list shows one row per lead.
  const openBookingModal = async (booking) => {
    setSelectedBooking(booking);
    setShowBookingModal(true);
    setActionMode(null);
    setRescheduleMode(false);
    setBookingHistory(booking._history || [booking]);
    setTransferHistory([]);
    if (booking.lead_id) {
      axios.get(`${API_URL}/api/calendar/bookings/lead-transfer-history?lead_id=${booking.lead_id}`,
        { withCredentials: true })
        .then((r) => setTransferHistory(r.data?.history || []))
        .catch(() => {});
    }
    setLoadingHistory(true);
    try {
      const params = new URLSearchParams();
      if (booking.lead_id) params.append('lead_id', booking.lead_id);
      if (booking.customer_email) params.append('email', booking.customer_email);
      const res = await axios.get(
        `${API_URL}/api/calendar/bookings/lead-history?${params.toString()}`,
        { withCredentials: true }
      );
      if (Array.isArray(res.data) && res.data.length) setBookingHistory(res.data);
    } catch {
      // keep the in-memory _history fallback
    } finally {
      setLoadingHistory(false);
    }
  };


  const handleBookingAction = async (action) => {
    if (!selectedBooking) return;
    
    const payload = { status: action };
    
    if (action === 'wip') {
      payload.sales_notes = actionNotes;
      if (callbackDate && callbackTime) {
        payload.callback_datetime = `${callbackDate}T${callbackTime}`;
      }
    } else if (action === 'not_interested' || action === 'no_show') {
      payload.sales_notes = actionNotes;
    }
    
    try {
      await axios.put(`${API_URL}/api/calendar/bookings/${selectedBooking.booking_id}`, payload, { withCredentials: true });
      const labels = { wip: 'WIP', not_interested: 'Not Interested', no_show: 'No Show', paid: 'Paid' };
      toast.success(`Marked as ${labels[action] || action}`);
      setShowBookingModal(false);
      setSelectedBooking(null);
      setActionMode(null);
      setActionNotes('');
      setCallbackDate('');
      setCallbackTime('');
      fetchSlotsAndBookings();
      fetchAllBookings();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const handleUpdateBooking = async (status) => {
    if (!selectedBooking) return;
    try {
      await axios.put(`${API_URL}/api/calendar/bookings/${selectedBooking.booking_id}`, { status }, { withCredentials: true });
      toast.success(`Booking ${status}`);
      setShowBookingModal(false);
      setSelectedBooking(null);
      setRescheduleMode(false);
      fetchSlotsAndBookings();
      fetchAllBookings();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  // V41 — Soft-cancel (all roles) / hard-delete (super admin only)
  const handleCancelBooking = async () => {
    if (!selectedBooking) return;
    if (!window.confirm('Cancel this consultation? The slot will be freed. The record stays in history.')) return;
    try {
      await axios.post(`${API_URL}/api/calendar/bookings/${selectedBooking.booking_id}/cancel`, {}, { withCredentials: true });
      toast.success('Consultation cancelled');
      setShowBookingModal(false);
      setSelectedBooking(null);
      setActionMode(null);
      fetchSlotsAndBookings();
      fetchAllBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel consultation');
    }
  };

  const handleDeleteBooking = async () => {
    if (!selectedBooking) return;
    if (!window.confirm('Permanently DELETE this consultation? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/calendar/bookings/${selectedBooking.booking_id}`, { withCredentials: true });
      toast.success('Consultation deleted permanently');
      setShowBookingModal(false);
      setSelectedBooking(null);
      setActionMode(null);
      fetchSlotsAndBookings();
      fetchAllBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete consultation');
    }
  };

  const handleStartReschedule = async () => {
    setRescheduleMode(true);
    setRescheduleDate('');
    setRescheduleTime('');
    setAvailableSlots([]);
  };

  const handleRescheduleDateChange = async (dateStr) => {
    setRescheduleDate(dateStr);
    setRescheduleTime('');
    if (!selectedBooking || !dateStr) return;

    setLoadingSlots(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/calendar/public/available-slots?event_type_id=${selectedBooking.event_type_id}&date_from=${dateStr}&date_to=${dateStr}`
      );
      setAvailableSlots(response.data.slots || []);
    } catch (error) {
      toast.error('Failed to load available slots');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleConfirmReschedule = async () => {
    if (!selectedBooking || !rescheduleDate || !rescheduleTime) {
      toast.error('Please select a date and time');
      return;
    }
    setRescheduling(true);
    try {
      await axios.put(
        `${API_URL}/api/calendar/bookings/${selectedBooking.booking_id}/reschedule`,
        { booking_date: rescheduleDate, booking_time: rescheduleTime },
        { withCredentials: true }
      );
      toast.success('Booking rescheduled successfully');
      setShowBookingModal(false);
      setSelectedBooking(null);
      setRescheduleMode(false);
      setRescheduleDate('');
      setRescheduleTime('');
      fetchSlotsAndBookings();
      fetchAllBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reschedule');
    } finally {
      setRescheduling(false);
    }
  };

  // Calendar helpers
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];
    const prevMonthLastDay = new Date(year, month, 0).getDate();

    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({ date: new Date(year, month - 1, prevMonthLastDay - i), isCurrentMonth: false });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }

    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }

    return days;
  };

  const getSlotsForDate = (date) => slots.filter(s => s.date === formatDate(date));
  const getBookingsForDate = (date) => bookings.filter(b => b.booking_date === formatDate(date));
  const isToday = (date) => date.toDateString() === new Date().toDateString();
  const isPast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  // V45.5 — capability split. `canManage` = create + delete event types AND
  // delete bookings → **Super Admin only**. `canEditEventType` = edit event
  // type availability, weekly schedule, holidays → Super Admin + Sales.
  // `canManageSlots` = open/close individual slots → Super Admin + Sales.
  const canManage = user?.role === 'super_admin';
  const canEditEventType = user?.role === 'super_admin' || user?.role === 'sales';
  const canManageSlots = user?.role === 'super_admin' || user?.role === 'sales';
  const days = getDaysInMonth();

  // Weekly schedule helpers for the form
  const toggleDayAvailability = (day) => {
    setEventTypeForm(prev => ({
      ...prev,
      weekly_schedule: {
        ...prev.weekly_schedule,
        [day]: {
          ...prev.weekly_schedule[day],
          enabled: !prev.weekly_schedule[day]?.enabled,
          slots: !prev.weekly_schedule[day]?.enabled ? [{ start: '09:00', end: '17:00' }] : []
        }
      }
    }));
  };

  const updateDaySlot = (day, slotIndex, field, value) => {
    setEventTypeForm(prev => {
      const newSlots = [...(prev.weekly_schedule[day]?.slots || [])];
      newSlots[slotIndex] = { ...newSlots[slotIndex], [field]: value };
      return {
        ...prev,
        weekly_schedule: {
          ...prev.weekly_schedule,
          [day]: { ...prev.weekly_schedule[day], slots: newSlots }
        }
      };
    });
  };

  const addDaySlot = (day) => {
    setEventTypeForm(prev => ({
      ...prev,
      weekly_schedule: {
        ...prev.weekly_schedule,
        [day]: {
          ...prev.weekly_schedule[day],
          slots: [...(prev.weekly_schedule[day]?.slots || []), { start: '13:00', end: '17:00' }]
        }
      }
    }));
  };

  const removeDaySlot = (day, slotIndex) => {
    setEventTypeForm(prev => ({
      ...prev,
      weekly_schedule: {
        ...prev.weekly_schedule,
        [day]: {
          ...prev.weekly_schedule[day],
          slots: prev.weekly_schedule[day].slots.filter((_, i) => i !== slotIndex)
        }
      }
    }));
  };

  const generateTimeOptions = () => {
    const times = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 30) {
        times.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      }
    }
    return times;
  };

  return (
    <div data-testid="calendar-page" className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-xl font-semibold text-slate-900">Calendar</h1>
          </div>

          {/* Tabs */}
          <div className="flex gap-1">
            {(canAccessCalendarAdmin ? [
              { id: 'event-types', label: 'Event Types' },
              { id: 'calendar', label: 'Calendar & Slots' },
              { id: 'bookings', label: 'Bookings' },
            ] : [
              { id: 'bookings', label: 'My Consultations' },
            ]).map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveView(tab.id)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeView === tab.id
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Event Types View - Super Admin + Sales (Sales can edit but not create/delete) */}
        {activeView === 'event-types' && canAccessCalendarAdmin && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Event Types</h2>
                <p className="text-sm text-slate-500">Each event type has its own independent calendar and availability settings</p>
              </div>
              {canManage && (
                <button
                  onClick={() => {
                    setEditingEventType(null);
                    resetEventTypeForm();
                    setShowEventTypeModal(true);
                  }}
                  className="btn-primary flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  New Event Type
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {eventTypes.map((event) => (
                <div
                  key={event.event_type_id}
                  className={`bg-white rounded-xl border-2 overflow-hidden hover:shadow-md transition-all ${
                    selectedEventType?.event_type_id === event.event_type_id 
                      ? EVENT_COLORS[event.color]?.border || 'border-indigo-500'
                      : 'border-slate-200'
                  }`}
                >
                  <div className={`h-2 ${EVENT_COLORS[event.color]?.bg || 'bg-indigo-500'}`} />
                  <div className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{event.name}</h3>
                        <p className="text-sm text-slate-500 mt-1">
                          {event.duration_minutes} min • {event.advance_booking_days} days advance
                        </p>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {event.country_filter && (
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${EVENT_COLORS[event.color]?.light} ${EVENT_COLORS[event.color]?.text}`}>
                              {event.country_filter}
                            </span>
                          )}
                          {event.allow_group_booking && (
                            <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                              Group • Max {event.max_group_size}
                            </span>
                          )}
                        </div>
                      </div>
                      {canEditEventType && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => setAutomationLogEvent(event)}
                            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                            title="Automation log — reminders actually sent"
                            data-testid={`automation-log-event-type-${event.event_type_id}`}
                          >
                            <History className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditEventType(event)}
                            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                            data-testid={`edit-event-type-${event.event_type_id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {canManage && (
                            <button
                              onClick={() => handleDeleteEventType(event.event_type_id)}
                              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                              data-testid={`delete-event-type-${event.event_type_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                      <button
                        onClick={() => {
                          setSelectedEventType(event);
                          setActiveView('calendar');
                        }}
                        className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                      >
                        Manage Calendar →
                      </button>
                      <button
                        onClick={() => copyBookingLink(event.slug)}
                        className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" />
                        Copy link
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {eventTypes.length === 0 && !loading && canManage && (
                <button
                  onClick={() => {
                    setEditingEventType(null);
                    resetEventTypeForm();
                    setShowEventTypeModal(true);
                  }}
                  className="bg-white rounded-xl border-2 border-dashed border-slate-300 p-8 flex flex-col items-center justify-center text-slate-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors"
                >
                  <Plus className="w-8 h-8 mb-2" />
                  <span className="font-medium">Create your first event type</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Calendar & Slots View - Admin Only */}
        {activeView === 'calendar' && canAccessCalendarAdmin && (
          <div className="space-y-6">
            {/* Event Type Selector */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <select
                  value={selectedEventType?.event_type_id || ''}
                  onChange={(e) => {
                    const et = eventTypes.find(x => x.event_type_id === e.target.value);
                    setSelectedEventType(et);
                  }}
                  className="input-base font-medium"
                >
                  {eventTypes.map(et => (
                    <option key={et.event_type_id} value={et.event_type_id}>{et.name}</option>
                  ))}
                </select>
                {selectedEventType && (
                  <span className={`px-2 py-1 rounded text-xs font-medium ${EVENT_COLORS[selectedEventType.color]?.light} ${EVENT_COLORS[selectedEventType.color]?.text}`}>
                    {selectedEventType.duration_minutes} min slots
                  </span>
                )}
              </div>
              {/* Info about dynamic slots */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-sm text-blue-700">
                Slots are automatically generated from your weekly availability settings
              </div>
            </div>

            {/* Holidays for selected event */}
            {selectedEventType && selectedEventType.holidays && selectedEventType.holidays.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-amber-600" />
                  <span className="font-medium text-amber-800">Holidays (No Bookings)</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedEventType.holidays.map(date => (
                    <span key={date} className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-sm">
                      {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 border border-slate-200">
                  <div className="text-2xl font-bold text-slate-900">{stats.total_slots_today}</div>
                  <div className="text-sm text-slate-500">Slots Today</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-slate-200">
                  <div className="text-2xl font-bold text-green-600">{stats.available_slots_today}</div>
                  <div className="text-sm text-slate-500">Available</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-slate-200">
                  <div className="text-2xl font-bold text-indigo-600">{stats.bookings_today}</div>
                  <div className="text-sm text-slate-500">Bookings Today</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-slate-200">
                  <div className="text-2xl font-bold text-amber-600">{stats.upcoming_bookings}</div>
                  <div className="text-sm text-slate-500">Upcoming (7 days)</div>
                </div>
              </div>
            )}

            {/* Calendar Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-slate-200">
                  <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} className="p-2 hover:bg-slate-100 rounded-lg">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <h3 className="text-lg font-semibold text-slate-900">
                    {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </h3>
                  <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} className="p-2 hover:bg-slate-100 rounded-lg">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>

                <div className="p-4">
                  <div className="grid grid-cols-7 mb-2">
                    {DAY_LABELS.map(d => (
                      <div key={d} className="text-center text-xs font-medium text-slate-500 py-2">{d}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {days.map((day, idx) => {
                      const dateSlots = getSlotsForDate(day.date);
                      const dateBookings = getBookingsForDate(day.date);
                      const isSelected = selectedDate && formatDate(selectedDate) === formatDate(day.date);

                      return (
                        <button
                          key={idx}
                          onClick={() => day.isCurrentMonth && setSelectedDate(day.date)}
                          className={`aspect-square p-1 rounded-lg relative transition-all ${
                            !day.isCurrentMonth ? 'opacity-40' :
                            isSelected ? 'bg-indigo-600 text-white' :
                            isToday(day.date) ? 'ring-2 ring-indigo-500 ring-offset-2' :
                            'hover:bg-slate-100'
                          }`}
                        >
                          <span className="text-sm font-medium">{day.date.getDate()}</span>
                          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                            {dateSlots.length > 0 && <div className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white/70' : 'bg-green-500'}`} />}
                            {dateBookings.length > 0 && <div className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white' : 'bg-indigo-500'}`} />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Selected Date Panel */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                {selectedDate ? (
                  <>
                    <div className="p-4 border-b border-slate-200 bg-slate-50">
                      <h3 className="font-semibold text-slate-900">
                        {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                      </h3>
                      <p className="text-sm text-slate-500">{getSlotsForDate(selectedDate).length} slots</p>
                    </div>
                    <div className="p-4 max-h-[400px] overflow-y-auto">
                      {getSlotsForDate(selectedDate).length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                          <Clock className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                          <p className="text-sm">No slots for this date</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {getSlotsForDate(selectedDate).map(slot => {
                            const slotBooking = bookings.find(b => b.slot_id === slot.slot_id);
                            return (
                              <div key={slot.slot_id} className={`p-3 rounded-lg border ${slotBooking ? 'bg-indigo-50 border-indigo-200' : 'bg-green-50 border-green-200'}`}>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <div className="font-medium text-sm">{formatTime(slot.start_time)} - {formatTime(slot.end_time)}</div>
                                    {slotBooking ? (
                                      <div
                                        className="text-xs text-indigo-600 cursor-pointer hover:underline"
                                        onClick={() => openBookingModal(slotBooking)}
                                      >
                                        {slotBooking.customer_name}
                                      </div>
                                    ) : (
                                      <div className="text-xs text-green-600">Available</div>
                                    )}
                                  </div>
                                  {!slotBooking && canManageSlots && (
                                    <button onClick={() => handleDeleteSlot(slot.slot_id)} className="p-1.5 text-slate-400 hover:text-red-500 rounded" data-testid={`delete-slot-${slot.slot_id}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="p-8 text-center text-slate-500">
                    <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>Select a date to view slots</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bookings View */}
        {activeView === 'bookings' && (
          <div className="space-y-6">
            {/* Filter Tabs */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex bg-slate-100 rounded-lg p-1">
                {[
                  { id: 'upcoming', label: 'Upcoming' },
                  { id: 'past', label: 'Past' },
                  // V36.6 — No Show + All tabs removed per user direction (Jun 2026).
                  // No-Show count remains visible on the Past tab outcome cards
                  // ("No-Show" bucket), so functionality is preserved.
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setBookingFilter(tab.id)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      bookingFilter === tab.id
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                    data-testid={`booking-tab-${tab.id}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              
              <select
                value={selectedEventType?.event_type_id || ''}
                onChange={(e) => {
                  const et = eventTypes.find(x => x.event_type_id === e.target.value);
                  setSelectedEventType(et || null);
                  // If "All Events" selected, clear selection but keep bookings fetched
                  if (!e.target.value) {
                    setSelectedEventType(null);
                  }
                }}
                className="input-base w-auto"
              >
                <option value="">All Events</option>
                {eventTypes.map(et => (
                  <option key={et.event_type_id} value={et.event_type_id}>{et.name}</option>
                ))}
              </select>
            </div>

            {/* V32 — Past-tab filters + outcome cards */}
            {bookingFilter === 'past' && (() => {
              const filteredPast = applyPastFilters(allBookings);
              const buckets = bucketPastBookings(filteredPast);
              const cards = [
                { key: 'pending',        label: 'Pending',        color: 'bg-slate-50 border-slate-300 text-slate-700', count: buckets.pending.length, hint: 'No action taken yet' },
                { key: 'wip',            label: 'WIP',            color: 'bg-amber-50 border-amber-200 text-amber-700', count: buckets.wip.length, hint: 'Rep marked WIP / follow-up' },
                { key: 'not_interested', label: 'Not Interested', color: 'bg-rose-50 border-rose-200 text-rose-700', count: buckets.not_interested.length },
                { key: 'no_show',        label: 'No-Show',        color: 'bg-slate-50 border-slate-200 text-slate-700', count: buckets.no_show.length },
                { key: 'paid',           label: 'Enrolled',       color: 'bg-emerald-50 border-emerald-200 text-emerald-700', count: buckets.paid.length },
              ];
              const repLabel = pastSalesRep === 'all' ? 'all reps' : (salesUsers.find(u => u.user_id === pastSalesRep)?.full_name || 'rep');
              // V36.9 — Unique leads (= sum of the 4 cards) vs raw consultation rows.
              const uniqueLeadCount = buckets.pending.length + buckets.not_interested.length + buckets.wip.length + buckets.no_show.length + buckets.paid.length;
              const rawConsultCount = filteredPast.length;
              return (
                <div className="space-y-4">
                  {/* Filters row */}
                  <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Period</label>
                      <select
                        value={pastDatePreset}
                        onChange={(e) => setPastDatePreset(e.target.value)}
                        className="input-base w-auto"
                        data-testid="past-date-preset"
                      >
                        <option value="today">Today</option>
                        <option value="yesterday">Yesterday</option>
                        <option value="mtd">Month to Date</option>
                        <option value="last_month">Last Month</option>
                        <option value="custom">Custom</option>
                      </select>
                    </div>
                    {pastDatePreset === 'custom' && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">From</label>
                          <input
                            type="date"
                            value={pastDateFrom}
                            onChange={(e) => setPastDateFrom(e.target.value)}
                            className="input-base w-auto"
                            data-testid="past-date-from"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">To</label>
                          <input
                            type="date"
                            value={pastDateTo}
                            onChange={(e) => setPastDateTo(e.target.value)}
                            className="input-base w-auto"
                            data-testid="past-date-to"
                          />
                        </div>
                      </>
                    )}
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Sales Associate</label>
                      <select
                        value={pastSalesRep}
                        onChange={(e) => setPastSalesRep(e.target.value)}
                        className="input-base w-auto"
                        data-testid="past-sales-rep"
                      >
                        <option value="all">All Associates</option>
                        {salesUsers.map(u => (
                          <option key={u.user_id} value={u.user_id}>
                            {u.full_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="ml-auto text-xs text-slate-500">
                      Showing <span className="font-semibold text-slate-700">{uniqueLeadCount}</span> unique {uniqueLeadCount === 1 ? 'lead' : 'leads'}{rawConsultCount !== uniqueLeadCount && <span className="text-slate-400"> · {rawConsultCount} consultations</span>} · {repLabel}
                    </div>
                  </div>

                  {/* Outcome cards */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {cards.map(c => (
                      <button
                        key={c.key}
                        onClick={() => setPastBucketModal({ key: c.key, label: c.label, leads: buckets[c.key] })}
                        className={`text-left rounded-xl border ${c.color} p-4 transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed`}
                        disabled={c.count === 0}
                        data-testid={`past-bucket-card-${c.key}`}
                      >
                        <div className="text-xs uppercase tracking-wide font-medium opacity-70">{c.label}</div>
                        <div className="text-3xl font-bold mt-1">{c.count}</div>
                        <div className="text-[11px] opacity-70 mt-1">
                          {c.hint || (c.count > 0 ? 'Click to view leads' : 'No leads')}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Filtered Bookings */}
            {(() => {
              const today = new Date();
              const now = new Date(); // Keep full datetime for comparison
              today.setHours(0, 0, 0, 0);
              
              // V36.6 — Compute upcoming date range when preset selected
              const getUpcomingDateRange = () => {
                const today0 = new Date(); today0.setHours(0, 0, 0, 0);
                const fmt = (d) => d.toISOString().slice(0, 10);
                if (upcomingDatePreset === 'today') {
                  return { from: fmt(today0), to: fmt(today0) };
                }
                if (upcomingDatePreset === 'tomorrow') {
                  const t = new Date(today0); t.setDate(t.getDate() + 1);
                  return { from: fmt(t), to: fmt(t) };
                }
                if (upcomingDatePreset === 'this_week') {
                  const end = new Date(today0); end.setDate(end.getDate() + 7);
                  return { from: fmt(today0), to: fmt(end) };
                }
                if (upcomingDatePreset === 'custom') {
                  return { from: upcomingDateFrom || null, to: upcomingDateTo || null };
                }
                return { from: null, to: null };
              };

              const filteredBookings = allBookings.filter(booking => {
                const bookingDate = new Date(booking.booking_date);
                bookingDate.setHours(0, 0, 0, 0);
                
                // Create full booking datetime for accurate comparison
                const [hours, minutes] = (booking.booking_time || '00:00').split(':');
                const bookingDateTime = new Date(booking.booking_date);
                bookingDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                
                if (bookingFilter === 'upcoming') {
                  // V36.6 — Apply upcoming date filter the SAME WAY past tab does
                  // (date filter wasn't honored before; this matches user request).
                  const { from, to } = getUpcomingDateRange();
                  if (from && booking.booking_date < from) return false;
                  if (to && booking.booking_date > to) return false;
                  if (canSeeAllSales && upcomingSalesRep !== 'all') {
                    const rid = upcomingSalesRep === 'me' ? user?.user_id : upcomingSalesRep;
                    if (booking.lead_assigned_sales_rep_id !== rid) return false;
                  }
                  return bookingDateTime > now && booking.status !== 'cancelled' && booking.status !== 'completed';
                } else if (bookingFilter === 'past') {
                  // V32 — Past: also respect the period + sales-rep filters from the cards above
                  const { from, to } = getPastDateRange();
                  if (from && booking.booking_date < from) return false;
                  if (to && booking.booking_date > to) return false;
                  if (pastSalesRep && pastSalesRep !== 'all' && booking.lead_assigned_sales_rep_id !== pastSalesRep) return false;
                  // Past: past datetime OR explicitly settled, not cancelled
                  return (bookingDateTime <= now || ['completed','wip','not_interested','no_show','paid'].includes(booking.status)) && booking.status !== 'cancelled';
                }
                return false; // V36.6 — no_show / all tabs removed
              });

              // V36.9 — Collapse to ONE unique row per lead. Past shows the latest
              // consultation (final outcome); Upcoming shows the soonest (next call).
              // Prior/rescheduled consultations move into the Booking History modal.
              const uniqueBookings = dedupeByLead(filteredBookings, bookingFilter === 'upcoming' ? 'earliest' : 'latest');

              // V36.6 — Sort by user-selected column + direction
              const sortedBookings = [...uniqueBookings].sort((a, b) => {
                let cmp = 0;
                if (bookingSortBy === 'datetime') {
                  const dtA = new Date(`${a.booking_date}T${a.booking_time || '00:00'}`);
                  const dtB = new Date(`${b.booking_date}T${b.booking_time || '00:00'}`);
                  cmp = dtA - dtB;
                } else if (bookingSortBy === 'customer') {
                  cmp = (a.customer_name || '').localeCompare(b.customer_name || '');
                } else if (bookingSortBy === 'status') {
                  cmp = (a.status || '').localeCompare(b.status || '');
                }
                return bookingSortDir === 'desc' ? -cmp : cmp;
              });

              // V36.6 — Paginate
              const totalBookings = sortedBookings.length;
              const totalPages = Math.max(1, Math.ceil(totalBookings / BOOKING_PAGE_SIZE));
              const pageBookings = sortedBookings.slice((bookingPage - 1) * BOOKING_PAGE_SIZE, bookingPage * BOOKING_PAGE_SIZE);

              return (
                <div className="space-y-3">
                  {/* V36.6 — Upcoming-tab date filter + sort controls.
                      Past tab keeps its existing filter row (already inside
                      the bookingFilter === 'past' branch above). */}
                  {bookingFilter === 'upcoming' && (
                    <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-3">
                      {canSeeAllSales && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-slate-500">Associate:</span>
                          <select
                            value={upcomingSalesRep}
                            onChange={(e) => { setUpcomingSalesRep(e.target.value); setBookingPage(1); }}
                            className="input-base text-sm w-auto"
                            data-testid="upcoming-sales-rep"
                          >
                            <option value="all">All associates</option>
                            <option value="me">My bookings</option>
                            {salesUsers.filter(u => u.user_id !== user?.user_id).map(u => (
                              <option key={u.user_id} value={u.user_id}>{u.full_name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-slate-500">Period:</span>
                        <select
                          value={upcomingDatePreset}
                          onChange={(e) => { setUpcomingDatePreset(e.target.value); setBookingPage(1); }}
                          className="input-base text-sm w-auto"
                          data-testid="upcoming-date-preset"
                        >
                          <option value="all">All upcoming</option>
                          <option value="today">Today</option>
                          <option value="tomorrow">Tomorrow</option>
                          <option value="this_week">Next 7 days</option>
                          <option value="custom">Custom</option>
                        </select>
                        {upcomingDatePreset === 'custom' && (
                          <>
                            <input
                              type="date"
                              value={upcomingDateFrom}
                              onChange={(e) => { setUpcomingDateFrom(e.target.value); setBookingPage(1); }}
                              className="input-base text-sm w-auto"
                              data-testid="upcoming-date-from"
                            />
                            <span className="text-slate-400">→</span>
                            <input
                              type="date"
                              value={upcomingDateTo}
                              onChange={(e) => { setUpcomingDateTo(e.target.value); setBookingPage(1); }}
                              className="input-base text-sm w-auto"
                              data-testid="upcoming-date-to"
                            />
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-auto">
                        <span className="text-xs font-medium text-slate-500">Sort:</span>
                        <select
                          value={`${bookingSortBy}|${bookingSortDir}`}
                          onChange={(e) => {
                            const [sb, sd] = e.target.value.split('|');
                            setBookingSortBy(sb); setBookingSortDir(sd);
                          }}
                          className="input-base text-sm w-auto"
                          data-testid="booking-sort"
                        >
                          <option value="datetime|asc">Date · Earliest first</option>
                          <option value="datetime|desc">Date · Latest first</option>
                          <option value="customer|asc">Customer · A → Z</option>
                          <option value="customer|desc">Customer · Z → A</option>
                          <option value="status|asc">Status · A → Z</option>
                        </select>
                      </div>
                    </div>
                  )}
                  {/* Past-tab sort lives inside its own filter row above.
                      Add a compact sort selector here for past too. */}
                  {bookingFilter === 'past' && (
                    <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-2 justify-end">
                      <span className="text-xs font-medium text-slate-500">Sort:</span>
                      <select
                        value={`${bookingSortBy}|${bookingSortDir}`}
                        onChange={(e) => {
                          const [sb, sd] = e.target.value.split('|');
                          setBookingSortBy(sb); setBookingSortDir(sd);
                        }}
                        className="input-base text-sm w-auto"
                        data-testid="booking-sort-past"
                      >
                        <option value="datetime|desc">Date · Latest first</option>
                        <option value="datetime|asc">Date · Earliest first</option>
                        <option value="customer|asc">Customer · A → Z</option>
                        <option value="status|asc">Status · A → Z</option>
                      </select>
                    </div>
                  )}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="overflow-x-auto">
                  <table className="w-full min-w-[680px]">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="p-4 text-left text-xs font-semibold text-slate-600 uppercase">Customer</th>
                        <th className="p-4 text-left text-xs font-semibold text-slate-600 uppercase">Event</th>
                        <th className="p-4 text-left text-xs font-semibold text-slate-600 uppercase">Date & Time</th>
                        {canSeeAllSales && <th className="p-4 text-left text-xs font-semibold text-slate-600 uppercase">Associate</th>}
                        <th className="p-4 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                        <th className="p-4 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageBookings.length === 0 ? (
                        <tr>
                          <td colSpan={canSeeAllSales ? 6 : 5} className="p-8 text-center text-slate-500">
                            No {bookingFilter} bookings found in this date range
                          </td>
                        </tr>
                      ) : (
                        pageBookings.map(booking => {
                          const eventType = eventTypes.find(e => e.event_type_id === booking.event_type_id);
                          return (
                            <tr key={booking.booking_id} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="p-4">
                                <div className="font-medium text-slate-900 flex items-center gap-2">
                                  {booking.customer_name}
                                  {booking._groupCount > 1 && (
                                    <span
                                      className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100"
                                      data-testid={`booking-history-count-${booking.booking_id}`}
                                      title={`${booking._groupCount} consultations for this lead`}
                                    >
                                      {booking._groupCount} consults
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-slate-500">{booking.customer_email}</div>
                              </td>
                              <td className="p-4">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${EVENT_COLORS[eventType?.color || 0]?.light} ${EVENT_COLORS[eventType?.color || 0]?.text}`}>
                                  {eventType?.name || 'Unknown'}
                                </span>
                              </td>
                              <td className="p-4" data-testid={`booking-time-${booking.booking_id}`}>
                                <div className="font-medium text-slate-900">
                                  {new Date(booking.booking_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                </div>
                                <div className="text-sm text-slate-600">
                                  {formatTime(booking.booking_time)} <span className="text-slate-400">IST</span>
                                </div>
                                {booking.user_timezone && booking.user_timezone !== 'Asia/Kolkata' && (
                                  <div className="text-xs text-indigo-600 mt-0.5" data-testid={`booking-user-tz-${booking.booking_id}`}>
                                    {convertTimezone(booking.booking_date, booking.booking_time, booking.booking_timezone || 'Asia/Kolkata', booking.user_timezone) || formatTime(booking.booking_time)}{' '}
                                    <span className="text-indigo-400">{getTimezoneAbbr(booking.user_timezone)}</span>
                                  </div>
                                )}
                              </td>
                              {canSeeAllSales && (
                                <td className="p-4 text-sm text-slate-700" data-testid={`booking-associate-${booking.booking_id}`}>
                                  {booking.lead_assigned_sales_rep_name || 'Unassigned'}
                                  {booking.conducted_by_name && booking.conducted_by_name !== booking.lead_assigned_sales_rep_name && (
                                    <div className="text-[11px] text-slate-400" title="Who took this consultation — the lead has since moved" data-testid={`booking-conducted-by-${booking.booking_id}`}>
                                      taken by {booking.conducted_by_name}
                                    </div>
                                  )}
                                </td>
                              )}
                              <td className="p-4">
                                {bookingFilter === 'past'
                                  ? <StatusBadge status={OUTCOME_STATUS[classifyOutcome(booking)]} size="xs" />
                                  : <StatusBadge status={booking.status || 'pending'} size="xs" />}
                              </td>
                              <td className="p-4">
                                <button
                                  onClick={() => openBookingModal(booking)}
                                  className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                                  data-testid={`booking-manage-${booking.booking_id}`}
                                >
                                  Manage
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                  </div>
                </div>
                {/* V36.6 — Pagination footer (applies to both Upcoming + Past) */}
                {totalBookings > BOOKING_PAGE_SIZE && (
                  <div className="bg-white border border-slate-200 rounded-xl px-4 py-2 flex items-center justify-between">
                    <span className="text-xs text-slate-500">
                      Showing {Math.min((bookingPage - 1) * BOOKING_PAGE_SIZE + 1, totalBookings)}–{Math.min(bookingPage * BOOKING_PAGE_SIZE, totalBookings)} of {totalBookings}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        disabled={bookingPage <= 1}
                        onClick={() => setBookingPage(p => Math.max(1, p - 1))}
                        className="px-2 py-1 text-xs rounded-md border border-slate-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50"
                        data-testid="booking-prev-page"
                      >
                        ← Prev
                      </button>
                      <span className="text-xs text-slate-600 px-1">Page {bookingPage} of {totalPages}</span>
                      <button
                        disabled={bookingPage >= totalPages}
                        onClick={() => setBookingPage(p => Math.min(totalPages, p + 1))}
                        className="px-2 py-1 text-xs rounded-md border border-slate-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50"
                        data-testid="booking-next-page"
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                )}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* V32 — Past consultation outcome bucket modal */}
      {pastBucketModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="past-bucket-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{pastBucketModal.label} Leads</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Consultations marked <span className="font-medium">{pastBucketModal.label}</span> · {pastBucketModal.leads.length} lead{pastBucketModal.leads.length === 1 ? '' : 's'}
                  {(() => {
                    const { from, to } = getPastDateRange();
                    return from && to ? <> · {from} → {to}</> : null;
                  })()}
                </p>
              </div>
              <button onClick={() => setPastBucketModal(null)} className="p-1.5 hover:bg-slate-100 rounded-md" data-testid="past-bucket-modal-close">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1">
              {pastBucketModal.leads.length === 0 ? (
                <div className="p-10 text-center text-slate-500 text-sm">No leads in this bucket for the selected period</div>
              ) : (
                <table className="w-full">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-xs uppercase text-slate-500">
                      <th className="px-4 py-2.5 text-left font-medium">Customer</th>
                      <th className="px-4 py-2.5 text-left font-medium">Phone</th>
                      <th className="px-4 py-2.5 text-left font-medium">Consultation</th>
                      <th className="px-4 py-2.5 text-left font-medium">Sales Rep</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pastBucketModal.leads.map(b => (
                      <tr key={b.booking_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`past-bucket-row-${b.booking_id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900 text-sm">{b.customer_name}</div>
                          <div className="text-xs text-slate-500">{b.customer_email}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700">{b.customer_phone}</td>
                        <td className="px-4 py-3 text-sm">
                          <div className="text-slate-700">
                            {new Date(b.booking_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </div>
                          <div className="text-xs text-slate-500">{b.booking_time} IST</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700">{b.lead_assigned_sales_rep_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
              <button onClick={() => setPastBucketModal(null)} className="btn-secondary text-sm">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Event Type Modal */}
      {showEventTypeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl my-8">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">
                {editingEventType ? 'Edit Event Type' : 'New Event Type'}
              </h3>
              <button onClick={() => setShowEventTypeModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Event Name *</label>
                  <input
                    type="text"
                    value={eventTypeForm.name}
                    onChange={(e) => setEventTypeForm({ ...eventTypeForm, name: e.target.value, slug: slugify(e.target.value) })}
                    placeholder="e.g., India Consultation"
                    className="input-base"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">URL Slug</label>
                  <input
                    type="text"
                    value={eventTypeForm.slug}
                    onChange={(e) => setEventTypeForm({ ...eventTypeForm, slug: e.target.value })}
                    className="input-base"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Country Filter</label>
                  <select
                    value={eventTypeForm.country_filter}
                    onChange={(e) => setEventTypeForm({ ...eventTypeForm, country_filter: e.target.value })}
                    className="input-base"
                  >
                    <option value="">All Countries</option>
                    <option value="India">India</option>
                    <option value="United States">United States</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Canada">Canada</option>
                  </select>
                </div>
              </div>

              {/* Duration & Booking Settings */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Booking Settings</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Duration</label>
                    <select value={eventTypeForm.duration_minutes} onChange={(e) => setEventTypeForm({ ...eventTypeForm, duration_minutes: parseInt(e.target.value) })} className="input-base">
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={45}>45 min</option>
                      <option value={60}>60 min</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Advance Booking (days)</label>
                    <input type="number" value={eventTypeForm.advance_booking_days} onChange={(e) => setEventTypeForm({ ...eventTypeForm, advance_booking_days: parseInt(e.target.value) })} className="input-base" min="1" max="90" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Min Notice (hours)</label>
                    <input type="number" value={eventTypeForm.minimum_notice_hours} onChange={(e) => setEventTypeForm({ ...eventTypeForm, minimum_notice_hours: parseInt(e.target.value) })} className="input-base" min="0" max="168" />
                  </div>
                </div>
              </div>

              {/* Group Booking */}
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={eventTypeForm.allow_group_booking}
                      onChange={(e) => setEventTypeForm({ ...eventTypeForm, allow_group_booking: e.target.checked, max_group_size: e.target.checked ? (eventTypeForm.max_group_size > 1 ? eventTypeForm.max_group_size : 5) : 1 })}
                      className="sr-only peer"
                      data-testid="allow-group-booking-toggle"
                    />
                    <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                  <span className="text-sm font-medium text-slate-700">Allow Group Booking</span>
                </div>
                {eventTypeForm.allow_group_booking && (
                  <div className="ml-12">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Max bookings per slot</label>
                    <input
                      type="number"
                      value={eventTypeForm.max_group_size}
                      onChange={(e) => setEventTypeForm({ ...eventTypeForm, max_group_size: Math.max(2, parseInt(e.target.value) || 2) })}
                      className="input-base w-24"
                      min="2"
                      max="100"
                      data-testid="max-group-size-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Multiple people can book the same time slot</p>
                  </div>
                )}
              </div>

              {/* Color */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Color</label>
                <div className="flex gap-2">
                  {EVENT_COLORS.map((color, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setEventTypeForm({ ...eventTypeForm, color: idx })}
                      className={`w-8 h-8 rounded-full ${color.bg} ${eventTypeForm.color === idx ? 'ring-2 ring-offset-2 ring-slate-400' : ''}`}
                    />
                  ))}
                </div>
              </div>

              {/* Weekly Availability */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Weekly Availability</h4>
                <p className="text-sm text-slate-500 mb-3">Set your available hours. Slots will automatically be available for booking based on this schedule.</p>
                <div className="space-y-3">
                  {DAYS.map((day, idx) => (
                    <div key={day} className="flex items-start gap-4">
                      <div className="w-24 flex items-center gap-2 pt-2">
                        <button
                          type="button"
                          onClick={() => toggleDayAvailability(day)}
                          className={`w-10 h-6 rounded-full relative transition-colors ${eventTypeForm.weekly_schedule[day]?.enabled ? 'bg-indigo-600' : 'bg-slate-200'}`}
                        >
                          <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${eventTypeForm.weekly_schedule[day]?.enabled ? 'left-5' : 'left-1'}`} />
                        </button>
                        <span className={`text-sm font-medium ${eventTypeForm.weekly_schedule[day]?.enabled ? 'text-slate-900' : 'text-slate-400'}`}>
                          {DAY_LABELS[idx]}
                        </span>
                      </div>
                      <div className="flex-1">
                        {eventTypeForm.weekly_schedule[day]?.enabled ? (
                          <div className="space-y-2">
                            {(eventTypeForm.weekly_schedule[day]?.slots || []).map((slot, slotIdx) => (
                              <div key={slotIdx} className="flex items-center gap-2">
                                <select value={slot.start} onChange={(e) => updateDaySlot(day, slotIdx, 'start', e.target.value)} className="px-2 py-1.5 text-sm border border-slate-300 rounded">
                                  {generateTimeOptions().map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                                <span className="text-slate-400">-</span>
                                <select value={slot.end} onChange={(e) => updateDaySlot(day, slotIdx, 'end', e.target.value)} className="px-2 py-1.5 text-sm border border-slate-300 rounded">
                                  {generateTimeOptions().map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                                {eventTypeForm.weekly_schedule[day].slots.length > 1 && (
                                  <button type="button" onClick={() => removeDaySlot(day, slotIdx)} className="p-1 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                                )}
                                {slotIdx === eventTypeForm.weekly_schedule[day].slots.length - 1 && (
                                  <button type="button" onClick={() => addDaySlot(day)} className="p-1 text-slate-400 hover:text-indigo-600"><Plus className="w-4 h-4" /></button>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400 pt-2 block">Unavailable</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Holidays */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Holidays (Blocked Dates)</h4>
                <p className="text-sm text-slate-500 mb-3">Mark specific dates as holidays when no bookings can be made for this event.</p>
                
                <div className="flex gap-2 mb-3">
                  <input
                    type="date"
                    value={newHolidayDate}
                    onChange={(e) => setNewHolidayDate(e.target.value)}
                    className="input-base flex-1"
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <button
                    type="button"
                    onClick={handleAddHoliday}
                    disabled={!newHolidayDate}
                    className="btn-primary px-4"
                  >
                    Add Holiday
                  </button>
                </div>

                {eventTypeForm.holidays.length > 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex flex-wrap gap-2">
                      {eventTypeForm.holidays.map(date => (
                        <span
                          key={date}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm"
                        >
                          {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          <button
                            type="button"
                            onClick={() => handleRemoveHoliday(date)}
                            className="text-amber-600 hover:text-amber-800"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-slate-400 text-center py-3 bg-slate-50 rounded-lg">
                    No holidays set
                  </div>
                )}
              </div>

              {/* V40 — Booking Page Content */}
              <div className="border-t border-slate-200 pt-5" data-testid="booking-page-content-section">
                <h4 className="text-sm font-semibold text-slate-800 mb-1">Booking Page Content</h4>
                <p className="text-xs text-slate-500 mb-4">Customize the public booking page for this event. Leave blank to use defaults.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Header subtitle</label>
                    <input
                      type="text" className="input-base" placeholder="Book Your Free Consultation"
                      value={eventTypeForm.page_subtitle}
                      onChange={(e) => setEventTypeForm({ ...eventTypeForm, page_subtitle: e.target.value })}
                      data-testid="et-page-subtitle"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Booking button (CTA)</label>
                    <input
                      type="text" className="input-base" placeholder="Book Consultation"
                      value={eventTypeForm.cta_text}
                      onChange={(e) => setEventTypeForm({ ...eventTypeForm, cta_text: e.target.value })}
                      data-testid="et-cta-text"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Confirmation title</label>
                    <input
                      type="text" className="input-base" placeholder="Booking Confirmed!"
                      value={eventTypeForm.confirmation_title}
                      onChange={(e) => setEventTypeForm({ ...eventTypeForm, confirmation_title: e.target.value })}
                      data-testid="et-confirmation-title"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Confirmation subtitle</label>
                    <input
                      type="text" className="input-base" placeholder="Your consultation has been scheduled successfully."
                      value={eventTypeForm.confirmation_subtitle}
                      onChange={(e) => setEventTypeForm({ ...eventTypeForm, confirmation_subtitle: e.target.value })}
                      data-testid="et-confirmation-subtitle"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">{'"What\'s Next?" heading'}</label>
                    <input
                      type="text" className="input-base" placeholder="What's Next?"
                      value={eventTypeForm.whats_next_title}
                      onChange={(e) => setEventTypeForm({ ...eventTypeForm, whats_next_title: e.target.value })}
                      data-testid="et-whatsnext-title"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Meeting / class link <span className="text-slate-400">(used as {'{{link}}'} in WhatsApp)</span></label>
                    <input
                      type="text" className="input-base" placeholder="https://zoom.us/j/..."
                      value={eventTypeForm.meeting_link}
                      onChange={(e) => setEventTypeForm({ ...eventTypeForm, meeting_link: e.target.value })}
                      data-testid="et-meeting-link"
                    />
                  </div>
                </div>
                {/* What's Next bullet list */}
                <div className="mt-4">
                  <label className="block text-xs font-medium text-slate-600 mb-1">{'"What\'s Next?" bullet points'}</label>
                  <div className="space-y-2">
                    {(eventTypeForm.whats_next_items || []).map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          type="text" className="input-base flex-1" value={item}
                          placeholder={`Bullet ${idx + 1}`}
                          onChange={(e) => {
                            const items = [...eventTypeForm.whats_next_items];
                            items[idx] = e.target.value;
                            setEventTypeForm({ ...eventTypeForm, whats_next_items: items });
                          }}
                          data-testid={`et-whatsnext-item-${idx}`}
                        />
                        <button
                          type="button"
                          onClick={() => setEventTypeForm({ ...eventTypeForm, whats_next_items: eventTypeForm.whats_next_items.filter((_, i) => i !== idx) })}
                          className="text-rose-500 hover:text-rose-700 p-1"
                          data-testid={`et-whatsnext-remove-${idx}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setEventTypeForm({ ...eventTypeForm, whats_next_items: [...(eventTypeForm.whats_next_items || []), ''] })}
                      className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                      data-testid="et-whatsnext-add"
                    >
                      + Add bullet point
                    </button>
                  </div>
                </div>
                {/* V41 — Confirmation-page CTA buttons (label + link) */}
                <div className="mt-4" data-testid="confirmation-buttons-section">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Confirmation buttons (with link)</label>
                  <p className="text-[11px] text-slate-400 mb-2">Shown on the confirmation page. e.g. label &quot;Join WhatsApp Group&quot; → the group invite link.</p>
                  <div className="space-y-2">
                    {(eventTypeForm.confirmation_buttons || []).map((btn, idx) => (
                      <div key={btn.id || idx} className="flex items-center gap-2">
                        <input
                          type="text" className="input-base flex-1 text-sm" value={btn.label || ''}
                          placeholder="Button label (e.g. Join WhatsApp Group)"
                          onChange={(e) => updateConfirmationButton(idx, { label: e.target.value })}
                          data-testid={`et-confbtn-label-${idx}`}
                        />
                        <input
                          type="url" className="input-base flex-1 text-sm" value={btn.url || ''}
                          placeholder="https://chat.whatsapp.com/..."
                          onChange={(e) => updateConfirmationButton(idx, { url: e.target.value })}
                          data-testid={`et-confbtn-url-${idx}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeConfirmationButton(idx)}
                          className="text-rose-500 hover:text-rose-700 p-1"
                          data-testid={`et-confbtn-remove-${idx}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addConfirmationButton}
                      className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                      data-testid="et-confbtn-add"
                    >
                      + Add button
                    </button>
                  </div>
                </div>
              </div>

              {/* V42 — Custom questions (answers saved to the lead) */}
              <div className="border-t border-slate-200 pt-5" data-testid="custom-questions-section">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Custom questions</label>
                <p className="text-[11px] text-slate-400 mb-3">Name, email, phone &amp; country are always asked. Add extra questions here — answers are saved to the lead. e.g. &quot;Trimester&quot; → dropdown of 1/2/3.</p>
                <div className="space-y-3">
                  {(eventTypeForm.custom_questions || []).map((q, idx) => (
                    <div key={q.id || idx} className="rounded-lg border border-slate-200 p-3 bg-slate-50/50">
                      <div className="flex items-center gap-2 mb-2">
                        <select
                          value={q.preset || ''}
                          onChange={(e) => applyQuestionPreset(idx, e.target.value)}
                          className="input-base text-sm w-40"
                          data-testid={`et-cq-preset-${idx}`}
                        >
                          <option value="">Preset…</option>
                          {QUESTION_PRESETS.map(p => (
                            <option key={p.preset} value={p.preset}>{p.preset === 'custom' ? 'Custom' : p.label}</option>
                          ))}
                        </select>
                        <input
                          type="text" className="input-base flex-1 text-sm" value={q.label || ''}
                          placeholder="Question label (e.g. Trimester)"
                          onChange={(e) => updateCustomQuestion(idx, { label: e.target.value })}
                          data-testid={`et-cq-label-${idx}`}
                        />
                        <button type="button" onClick={() => removeCustomQuestion(idx)} className="text-rose-500 hover:text-rose-700 p-1" data-testid={`et-cq-remove-${idx}`}>
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <select
                          value={q.type || 'text'}
                          onChange={(e) => updateCustomQuestion(idx, { type: e.target.value })}
                          className="input-base text-sm w-32"
                          data-testid={`et-cq-type-${idx}`}
                        >
                          {QUESTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        {q.type === 'select' && (
                          <input
                            type="text" className="input-base flex-1 text-sm min-w-[200px]"
                            value={(q.options || []).join(', ')}
                            placeholder="Options, comma separated (e.g. Trimester 1, Trimester 2, Trimester 3)"
                            onChange={(e) => updateCustomQuestion(idx, { options: e.target.value.split(',').map(o => o.trimStart()) })}
                            data-testid={`et-cq-options-${idx}`}
                          />
                        )}
                        <label className="flex items-center gap-1.5 text-xs text-slate-600 ml-auto">
                          <input type="checkbox" checked={!!q.required} onChange={(e) => updateCustomQuestion(idx, { required: e.target.checked })} data-testid={`et-cq-required-${idx}`} />
                          Required
                        </label>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addCustomQuestion}
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                    data-testid="et-cq-add"
                  >
                    + Add question
                  </button>
                </div>
              </div>

              {/* V40 — WhatsApp Automations (WATI) */}
              <div className="border-t border-slate-200 pt-5" data-testid="whatsapp-automations-section">
                <h4 className="text-sm font-semibold text-slate-800 mb-1">WhatsApp Automations (WATI)</h4>
                <p className="text-xs text-slate-500 mb-4">
                  Pick a trigger and enter an approved WATI template name. Variables auto-filled: name, event name, date, time (recipient&apos;s timezone), link.
                  {!editingEventType && <span className="text-amber-600"> Save the event first to enable test sends.</span>}
                </p>
                <div className="space-y-3">
                  {(eventTypeForm.whatsapp_automations || []).map((a) => (
                    <div key={a.id} className="border border-slate-200 rounded-lg p-3 bg-slate-50" data-testid={`wa-automation-${a.id}`}>
                      <div className="flex flex-wrap items-center gap-3">
                        <label className="flex items-center gap-1.5 text-xs text-slate-600">
                          <input
                            type="checkbox" checked={!!a.enabled}
                            onChange={(e) => updateWhatsAppAutomation(a.id, { enabled: e.target.checked })}
                            data-testid={`wa-enabled-${a.id}`}
                          />
                          Enabled
                        </label>
                        <select
                          className="input-base !w-auto text-sm" value={a.trigger}
                          onChange={(e) => updateWhatsAppAutomation(a.id, { trigger: e.target.value })}
                          data-testid={`wa-trigger-${a.id}`}
                        >
                          {WA_TRIGGERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <input
                          type="text" className="input-base flex-1 min-w-[180px] text-sm"
                          placeholder="WATI template name"
                          value={a.template_name}
                          onChange={(e) => updateWhatsAppAutomation(a.id, { template_name: e.target.value })}
                          data-testid={`wa-template-${a.id}`}
                        />
                        <button
                          type="button" onClick={() => removeWhatsAppAutomation(a.id)}
                          className="text-rose-500 hover:text-rose-700 p-1"
                          data-testid={`wa-remove-${a.id}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <input
                          type="tel" className="input-base !w-auto text-sm" placeholder="Test phone (+1...)"
                          value={waTestPhone[a.id] || ''}
                          onChange={(e) => setWaTestPhone({ ...waTestPhone, [a.id]: e.target.value })}
                          data-testid={`wa-test-phone-${a.id}`}
                        />
                        <button
                          type="button" onClick={() => handleTestWhatsApp(a)}
                          disabled={waTestingId === a.id || !editingEventType}
                          className="px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid={`wa-test-btn-${a.id}`}
                        >
                          {waTestingId === a.id ? 'Sending…' : 'Send test'}
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button" onClick={addWhatsAppAutomation}
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                    data-testid="wa-add-automation"
                  >
                    + Add WhatsApp automation
                  </button>
                </div>
              </div>

            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button onClick={() => setShowEventTypeModal(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleSaveEventType} className="btn-primary flex items-center gap-2">
                <Save className="w-4 h-4" />
                {editingEventType ? 'Update' : 'Create'} Event Type
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Slots Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">Generate Slots</h3>
              <p className="text-sm text-slate-500 mt-1">
                Generate slots for <strong>{selectedEventType?.name}</strong> based on its weekly availability
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">From</label>
                  <input type="date" value={generateForm.date_from} onChange={(e) => setGenerateForm({ ...generateForm, date_from: e.target.value })} className="input-base" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">To</label>
                  <input type="date" value={generateForm.date_to} onChange={(e) => setGenerateForm({ ...generateForm, date_to: e.target.value })} className="input-base" />
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button onClick={() => setShowGenerateModal(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleGenerateSlots} disabled={!generateForm.date_from || !generateForm.date_to} className="btn-primary">Generate</button>
            </div>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && selectedBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
            <div className={`h-2 flex-shrink-0 ${EVENT_COLORS[eventTypes.find(e => e.event_type_id === selectedBooking.event_type_id)?.color || 0]?.bg}`} />
            <div className="p-6 flex-1 overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-slate-900">
                  {rescheduleMode ? 'Reschedule Booking' : 'Booking Details'}
                </h3>
                <button onClick={() => { setShowBookingModal(false); setSelectedBooking(null); setRescheduleMode(false); setBookingHistory([]); }} className="p-1 hover:bg-slate-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Customer Info */}
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-semibold text-indigo-600">{selectedBooking.customer_name.charAt(0)}</span>
                  </div>
                  <div>
                    <div className="font-medium text-slate-900">{selectedBooking.customer_name}</div>
                    <div className="text-sm text-slate-500">{selectedBooking.customer_email}</div>
                  </div>
                </div>

                {/* Current booking info */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm" data-testid="booking-details-panel">
                  <div className="flex justify-between"><span className="text-slate-500">{rescheduleMode ? 'Current Date' : 'Date'}</span><span className="font-medium">{selectedBooking.booking_date}</span></div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">{rescheduleMode ? 'Current Time' : 'Time (IST)'}</span>
                    <span className="font-medium">{formatTime(selectedBooking.booking_time)}</span>
                  </div>
                  {selectedBooking.user_timezone && selectedBooking.user_timezone !== 'Asia/Kolkata' && (
                    <div className="flex justify-between" data-testid="booking-modal-user-tz">
                      <span className="text-indigo-500">User Time ({getTimezoneAbbr(selectedBooking.user_timezone)})</span>
                      <span className="font-medium text-indigo-600">
                        {convertTimezone(selectedBooking.booking_date, selectedBooking.booking_time, selectedBooking.booking_timezone || 'Asia/Kolkata', selectedBooking.user_timezone) || formatTime(selectedBooking.booking_time)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between"><span className="text-slate-500">Phone</span><span className="font-medium">{selectedBooking.customer_phone}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Country</span><span className="font-medium">{selectedBooking.customer_country}</span></div>
                  <div className="flex justify-between" data-testid="booking-modal-associate"><span className="text-slate-500">Sales Associate</span><span className="font-medium">{selectedBooking.lead_assigned_sales_rep_name || 'Unassigned'}</span></div>
                  {selectedBooking.conducted_by_name && selectedBooking.conducted_by_name !== selectedBooking.lead_assigned_sales_rep_name && (
                    <div className="flex justify-between" data-testid="booking-modal-conducted-by">
                      <span className="text-slate-500">Consultation taken by</span>
                      <span className="font-medium text-slate-500">{selectedBooking.conducted_by_name}</span>
                    </div>
                  )}
                </div>

                {!rescheduleMode && canSeeAllSales && selectedBooking.lead_id && (user?.role === 'super_admin' || !selectedBooking.transfer_locked) && (
                  <div className="border border-amber-200 bg-amber-50/60 rounded-lg p-3" data-testid="booking-reassign-panel">
                    <div className="text-xs font-semibold text-amber-800 mb-2">Transfer this lead to another associate</div>
                    <div className="flex items-center gap-2">
                      <select value={reassignTo} onChange={(e) => setReassignTo(e.target.value)} className="input-base text-sm flex-1" data-testid="booking-reassign-select">
                        <option value="">Select associate…</option>
                        {salesUsers.filter(u => u.user_id !== selectedBooking.lead_assigned_sales_rep_id).map(u => (
                          <option key={u.user_id} value={u.user_id}>{u.full_name}{u.user_id === user?.user_id ? ' (me)' : ''}</option>
                        ))}
                      </select>
                      <button
                        disabled={!reassignTo || reassigning}
                        onClick={async () => {
                          setReassigning(true);
                          try {
                            await axios.post(`${API_URL}/api/leads/${selectedBooking.lead_id}/reassign`, { role: 'sales', to_user_id: reassignTo, note: 'From booking details' }, { withCredentials: true });
                            toast.success('Lead transferred — every consultation now shows the new owner');
                            setReassignTo('');
                            setShowBookingModal(false); setSelectedBooking(null);
                            fetchAllBookings();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Transfer failed');
                          } finally { setReassigning(false); }
                        }}
                        className="px-3 py-1.5 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                        data-testid="booking-reassign-btn"
                      >
                        {reassigning ? 'Transferring…' : 'Transfer'}
                      </button>
                    </div>
                    <p className="text-[11px] text-amber-700 mt-1.5">Recorded in the lead's ownership history and reflected on the lead and every consultation. The new owner is emailed.</p>
                    {selectedBooking.transfer_locked && (
                      <p className="text-[11px] text-amber-800 font-medium mt-1" data-testid="booking-reassign-superadmin-note">
                        Super Admin override — {selectedBooking.transfer_lock_reason}. Payments collected on this lead move to the new owner.
                      </p>
                    )}
                  </div>
                )}

                {!rescheduleMode && canSeeAllSales && selectedBooking.lead_id && selectedBooking.transfer_locked && user?.role !== 'super_admin' && (
                  <div className="border border-slate-200 bg-slate-50 rounded-lg p-3 flex gap-2" data-testid="booking-reassign-locked">
                    <LockIcon className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                    <p className="text-[11px] text-slate-600">
                      <span className="font-semibold text-slate-700">Transfer locked.</span> Only a Super Admin can change the owner now — {selectedBooking.transfer_lock_reason}.
                    </p>
                  </div>
                )}

                {!rescheduleMode && selectedBooking.lead_id && (
                  <div className="border border-slate-200 rounded-lg p-3" data-testid="booking-transfer-history">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Lead Transfer History</div>
                      <span className="text-[11px] text-slate-400">{transferHistory.length} change{transferHistory.length === 1 ? '' : 's'}</span>
                    </div>
                    {transferHistory.length === 0 ? (
                      <p className="text-xs text-slate-400" data-testid="booking-transfer-history-empty">No transfers — the first owner still holds this lead.</p>
                    ) : (
                      <ol className="space-y-2 max-h-40 overflow-y-auto pr-1">
                        {transferHistory.map((h, i) => (
                          <li key={`${h.at}-${i}`} className="text-xs" data-testid={`booking-transfer-row-${i}`}>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${h.role === 'sales' ? 'bg-indigo-50 text-indigo-700' : 'bg-slate-100 text-slate-600'}`}>
                                {h.role === 'sales' ? 'Sales' : 'Pre-Sales'}
                              </span>
                              <span className="text-slate-500">{h.from_name || 'Unassigned'}</span>
                              <ArrowRight className="w-3 h-3 text-slate-400" />
                              <span className="font-medium text-slate-800">{h.to_name || 'Unassigned'}</span>
                            </div>
                            <div className="text-[11px] text-slate-400 mt-0.5">
                              {h.reason_label || h.reason}{h.by_name ? ` · by ${h.by_name}` : ''}
                              {h.at ? ` · ${new Date(h.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}
                            </div>
                            {(h.payments_moved > 0 || h.bookings_moved > 0) && (
                              <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`booking-transfer-moved-${i}`}>
                                {h.payments_moved > 0 && `${h.payments_moved} payment${h.payments_moved === 1 ? '' : 's'} moved`}
                                {h.payments_moved > 0 && h.revenue_moved > 0 && ` · ₹${Number(h.revenue_moved).toLocaleString('en-IN')} revenue`}
                                {h.payments_moved > 0 && !h.revenue_moved && ' · no revenue credit (trial / add-on only)'}
                                {h.bookings_moved > 0 && `${h.payments_moved > 0 ? ' · ' : ''}${h.bookings_moved} consultation${h.bookings_moved === 1 ? '' : 's'}`}
                              </div>
                            )}
                            {h.note && <div className="text-[11px] text-slate-500 italic">“{h.note}”</div>}
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                )}

                {/* V36.10 — Outcome reason / sales notes captured by the rep when
                    marking Not Interested / No Show / WIP. Falls back to the lead's
                    note for legacy rows. */}
                {!rescheduleMode && (selectedBooking.outcome_reason || selectedBooking.can_edit_sales_notes) && (
                  <div
                    className="rounded-lg border border-amber-200 bg-amber-50 p-3"
                    data-testid="booking-outcome-reason"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                        {(selectedBooking.lead_status === 'not_interested' || selectedBooking.status === 'not_interested')
                          ? 'Reason · Not Interested'
                          : (selectedBooking.lead_status === 'no_show' || selectedBooking.status === 'no_show')
                            ? 'No-Show Notes'
                            : 'Sales Notes'}
                      </div>
                      {selectedBooking.can_edit_sales_notes && !editingNotes && (
                        <button type="button" onClick={() => { setNotesDraft(selectedBooking.outcome_reason || ''); setEditingNotes(true); }}
                          className="text-[11px] font-semibold text-amber-700 hover:text-amber-900 inline-flex items-center gap-1"
                          data-testid="booking-notes-edit-btn">
                          <Edit2 className="w-3 h-3" /> {selectedBooking.outcome_reason ? 'Edit' : 'Add note'}
                        </button>
                      )}
                    </div>
                    {editingNotes ? (
                      <div>
                        <textarea value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} rows={3}
                          placeholder="What did she say on the call?"
                          className="w-full text-sm border border-amber-300 rounded-md p-2 focus:outline-none focus:ring-1 focus:ring-amber-400"
                          data-testid="booking-notes-textarea" />
                        <div className="flex gap-2 mt-2">
                          <button type="button" onClick={saveSalesNotes} disabled={savingNotes}
                            className="px-3 py-1.5 text-xs font-semibold rounded-md bg-amber-600 text-white disabled:opacity-50"
                            data-testid="booking-notes-save-btn">{savingNotes ? 'Saving…' : 'Save note'}</button>
                          <button type="button" onClick={() => setEditingNotes(false)}
                            className="px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-300 text-slate-600"
                            data-testid="booking-notes-cancel-btn">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="booking-notes-text">
                        {selectedBooking.outcome_reason || <span className="text-slate-400 italic">No notes yet — add what she said on the call.</span>}
                      </p>
                    )}
                    {selectedBooking.sales_notes_updated_by_name && !editingNotes && (
                      <p className="text-[11px] text-amber-700/80 mt-1" data-testid="booking-notes-meta">
                        Last edited by {selectedBooking.sales_notes_updated_by_name}
                      </p>
                    )}
                  </div>
                )}

                {/* V36.9 — Booking History: every consultation for this lead, so
                    rescheduled / repeat consultations stay visible even though the
                    list now shows ONE unique row per lead. */}
                {!rescheduleMode && (
                  <div data-testid="booking-history-section">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Booking History</h4>
                      {!loadingHistory && bookingHistory.length > 0 && (
                        <span className="text-[11px] text-slate-400">{bookingHistory.length} total</span>
                      )}
                    </div>
                    {loadingHistory ? (
                      <div className="flex justify-center py-3">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                      </div>
                    ) : bookingHistory.length <= 1 ? (
                      <p className="text-xs text-slate-400 py-1">No previous consultations for this lead.</p>
                    ) : (
                      <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
                        {bookingHistory.map((h) => {
                          const et = eventTypes.find(e => e.event_type_id === h.event_type_id);
                          const isCurrent = h.booking_id === selectedBooking.booking_id;
                          return (
                            <div
                              key={h.booking_id}
                              className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs ${isCurrent ? 'border-indigo-200 bg-indigo-50' : 'border-slate-100 bg-white'}`}
                              data-testid={`history-row-${h.booking_id}`}
                            >
                              <div className="min-w-0">
                                <div className="font-medium text-slate-800">
                                  {new Date(h.booking_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  {' · '}{formatTime(h.booking_time)}
                                  {isCurrent && <span className="ml-1 text-indigo-500">(this one)</span>}
                                </div>
                                <div className="text-slate-400 truncate">{et?.name || 'Consultation'}</div>
                                {h.sales_notes && (
                                  <div className="text-slate-500 italic mt-0.5 line-clamp-2">“{h.sales_notes}”</div>
                                )}
                              </div>
                              <StatusBadge status={h.status || 'pending'} size="xs" />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* V42.4 — Enrolled = locked. A paid/enrolled customer's
                    consultation cannot be edited, rescheduled, cancelled or
                    deleted (data sanctity). */}
                {classifyOutcome(selectedBooking) === 'paid' && !rescheduleMode && !actionMode && (
                  <div className="mt-4 p-3 rounded-lg border border-emerald-300 bg-emerald-50 flex items-start gap-2" data-testid="enrolled-locked-banner">
                    <LockIcon className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-emerald-800">
                      <span className="font-semibold">Enrolled — consultation locked.</span>{' '}
                      This customer has already enrolled, so status changes, reschedule, cancel and delete are disabled to protect the record.
                    </div>
                  </div>
                )}

                {/* Reschedule Flow */}
                {rescheduleMode ? (
                  <div className="space-y-4" data-testid="reschedule-panel">
                    {/* Date Picker */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">New Date</label>
                      <input
                        type="date"
                        value={rescheduleDate}
                        onChange={(e) => handleRescheduleDateChange(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="input-base"
                        data-testid="reschedule-date-input"
                      />
                    </div>

                    {/* Available Time Slots */}
                    {rescheduleDate && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Available Time Slots</label>
                        {loadingSlots ? (
                          <div className="flex justify-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                          </div>
                        ) : availableSlots.length === 0 ? (
                          <div className="text-center py-4 text-sm text-slate-500 bg-slate-50 rounded-lg">
                            No available slots for this date. Try another date.
                          </div>
                        ) : (
                          <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto" data-testid="reschedule-slots">
                            {availableSlots.map((slot) => (
                              <button
                                key={slot.slot_id}
                                onClick={() => setRescheduleTime(slot.start_time)}
                                className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                                  rescheduleTime === slot.start_time
                                    ? 'bg-indigo-600 text-white border-indigo-600'
                                    : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-400 hover:text-indigo-600'
                                }`}
                                data-testid={`slot-${slot.start_time}`}
                              >
                                {formatTime(slot.start_time)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-4 border-t border-slate-200">
                      <button
                        onClick={() => { setRescheduleMode(false); setRescheduleDate(''); setRescheduleTime(''); }}
                        className="flex-1 btn-secondary"
                      >
                        Back
                      </button>
                      <button
                        onClick={handleConfirmReschedule}
                        disabled={!rescheduleDate || !rescheduleTime || rescheduling}
                        className="flex-1 btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid="confirm-reschedule-btn"
                      >
                        <RefreshCw className={`w-4 h-4 ${rescheduling ? 'animate-spin' : ''}`} />
                        {rescheduling ? 'Rescheduling...' : 'Confirm Reschedule'}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Default Actions — 5 buttons (hidden when lead is Enrolled) */
                  selectedBooking.status === 'confirmed' && !actionMode && classifyOutcome(selectedBooking) !== 'paid' && (
                    <div className="flex flex-wrap gap-2 pt-4">
                      <button onClick={() => { setActionMode('wip'); setActionNotes(''); setCallbackDate(''); setCallbackTime(''); }}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium" data-testid="wip-booking-btn">
                        WIP
                      </button>
                      <button onClick={() => { setActionMode('not_interested'); setActionNotes(''); }}
                        className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 text-sm font-medium" data-testid="not-interested-booking-btn">
                        Not Interested
                      </button>
                      <button
                        onClick={handleStartReschedule}
                        className="flex-1 px-4 py-2 text-amber-600 border border-amber-300 hover:bg-amber-50 rounded-lg text-sm font-medium flex items-center justify-center gap-1"
                        data-testid="reschedule-btn"
                      >
                        <RefreshCw className="w-4 h-4" /> Reschedule
                      </button>
                      <button onClick={() => { setActionMode('no_show'); setActionNotes(''); }}
                        className="flex-1 px-4 py-2 text-red-600 border border-red-300 hover:bg-red-50 rounded-lg text-sm font-medium" data-testid="noshow-booking-btn">
                        No Show
                      </button>
                    </div>
                  )
                )}

                {/* WIP Action Form */}
                {actionMode === 'wip' && (
                  <div className="space-y-3 pt-4 border-t border-slate-200" data-testid="wip-form">
                    <h4 className="text-sm font-semibold text-slate-700">Work In Progress</h4>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Sales Notes</label>
                      <textarea value={actionNotes} onChange={(e) => setActionNotes(e.target.value)}
                        placeholder="Update notes about this lead..." className="input-base h-20 text-sm" data-testid="wip-notes" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Callback Date</label>
                        <input type="date" value={callbackDate} onChange={(e) => setCallbackDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]} className="input-base text-sm" data-testid="wip-callback-date" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Callback Time</label>
                        <input type="time" value={callbackTime} onChange={(e) => setCallbackTime(e.target.value)}
                          className="input-base text-sm" data-testid="wip-callback-time" />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setActionMode(null)} className="flex-1 btn-secondary text-sm">Back</button>
                      <button onClick={() => handleBookingAction('wip')} className="flex-1 btn-primary text-sm" data-testid="wip-confirm-btn">Save & Mark WIP</button>
                    </div>
                  </div>
                )}

                {/* Not Interested Action Form */}
                {actionMode === 'not_interested' && (
                  <div className="space-y-3 pt-4 border-t border-slate-200" data-testid="not-interested-form">
                    <h4 className="text-sm font-semibold text-slate-700">Not Interested</h4>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Sales Notes (reason)</label>
                      <textarea value={actionNotes} onChange={(e) => setActionNotes(e.target.value)}
                        placeholder="Why is this lead not interested?" className="input-base h-20 text-sm" data-testid="not-interested-notes" />
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setActionMode(null)} className="flex-1 btn-secondary text-sm">Back</button>
                      <button onClick={() => handleBookingAction('not_interested')} className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 text-sm font-medium" data-testid="not-interested-confirm-btn">Mark Not Interested</button>
                    </div>
                  </div>
                )}

                {/* No Show Action Form */}
                {actionMode === 'no_show' && (
                  <div className="space-y-3 pt-4 border-t border-slate-200" data-testid="no-show-form">
                    <h4 className="text-sm font-semibold text-slate-700">No Show</h4>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Sales Notes</label>
                      <textarea value={actionNotes} onChange={(e) => setActionNotes(e.target.value)}
                        placeholder="Any notes about the no show..." className="input-base h-20 text-sm" data-testid="no-show-notes" />
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setActionMode(null)} className="flex-1 btn-secondary text-sm">Back</button>
                      <button onClick={() => handleBookingAction('no_show')} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium" data-testid="no-show-confirm-btn">Mark No Show</button>
                    </div>
                  </div>
                )}

                {/* V41 — Cancel (all roles) / Delete (super admin only).
                    V42.4 — hidden entirely once the lead is Enrolled. */}
                {!rescheduleMode && !actionMode && classifyOutcome(selectedBooking) !== 'paid' && (
                  <div className="flex flex-wrap items-center gap-2 pt-4 mt-2 border-t border-slate-200" data-testid="booking-manage-actions">
                    {selectedBooking.status !== 'cancelled' && (
                      <button
                        onClick={handleCancelBooking}
                        className="flex-1 px-4 py-2 text-orange-600 border border-orange-300 hover:bg-orange-50 rounded-lg text-sm font-medium"
                        data-testid="cancel-booking-btn"
                      >
                        Cancel consultation
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={handleDeleteBooking}
                        className="flex-1 px-4 py-2 text-red-700 border border-red-300 hover:bg-red-50 rounded-lg text-sm font-medium flex items-center justify-center gap-1"
                        data-testid="delete-booking-btn"
                      >
                        <Trash2 className="w-4 h-4" /> Delete permanently
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {automationLogEvent && (
        <AutomationLogModal eventType={automationLogEvent} onClose={() => setAutomationLogEvent(null)} />
      )}
    </div>
  );
};

export default CalendarPage;
